import {
  Column,
  type ColumnDef,
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type RowData,
  type SortingState,
  type Table,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import {
  type ComponentType,
  createContext,
  CSSProperties,
  type PropsWithChildren,
  ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import { DateRange } from '../dates/RangeCalendar';

const DEFAULT_PAGE_SIZES: number[] = [50, 100, 150];

// Generic Filter Date Type
// the dropdown filter is not opinionated about the type of data
export type FilterDataType = any;

export type FilterOption<T = FilterDataType> = {
  id: string;
  label?: string | React.ReactNode;
  value: T;
};

type FilterSearchFn = <T = FilterDataType>(
  search: string,
  options: FilterOption<T>[],
) => FilterOption<T>[];

type FilterProps<T = FilterDataType> = {
  title: string;
  isDisabled?: boolean;
  options: FilterOption<T>[];
  defaultSelectedIds?: string[];
  selectedIds?: string[];
  showSearch?: boolean;
  showSelectAll?: boolean;
  onChange?: (selections: FilterOption<T>[]) => void;
  onClose?: (selections: FilterOption<T>[]) => void;
  handleSearch?: FilterSearchFn;
};

export type DateRangeFilter = {
  type: 'dateRange';
  id: string;
  value?: DateRange;
};

export type CheckboxFilter = {
  type: 'checkbox';
  id: string;
  label: string;
  onChange?: (checked: boolean) => void;
};

export type DataTableFilter<T = FilterDataType> =
  | (Omit<
      FilterProps<T> & { multiselect?: boolean },
      'defaultSelectedIds' | 'selectedIds'
    > & {
      id: string;
      type: 'select';
    })
  | DateRangeFilter
  | CheckboxFilter;

export type FilterValues = Record<string, (string | number)[] | DateRange>;

// Define the shape of your DataTable context
export type DataTableContextProps<T extends RowData> = PropsWithChildren<
  {
    columns: ColumnDef<T, any>[];
    data: T[];
    defaultFilterValues?: FilterValues;
    controlledFilterState?: Record<string, FilterOption[] | FilterOption>; // Controlled filter state
    enableFilters?: boolean;
    enablePagination?: boolean;
    enableSearch?: boolean;
    searchPlaceholder?: string;
    searchValue?: string; // Add controlled searchValue
    filters?: DataTableFilter[];
    initialPageIndex?: number;
    initialPageSize?: number;
    pageIndex?: number; // Add controlled pageIndex
    pageSize?: number; // Add controlled pageSize
    loading?: boolean;
    pageSizeOptions?: number[];
    rowCount?: number;
    searchDebounceMs?: number;
    showError?: boolean;
    enableSorting?: boolean;
    initialSorting?: SortingState;
    autoResetPageIndex?: boolean;
    showHeader?: boolean;
    title?: string;
    isEditing?: boolean;
    pinColumns?: ColumnPinningState;
    onUpdateData?: (rowIndex: number, columnId: string, value: unknown) => void;
    renderHeaderActions?: () => ReactNode;
    EmptySearchResultsPlaceholder?: ComponentType<{ clearSearch: () => void }>;
    ErrorPlaceholder?: ComponentType;
    NoDataPlaceholder?: ComponentType;
    onFilterChange?: (filters: Record<string, FilterDataType[]>) => void;
    onFilterStateChange?: (
      filterState: Record<string, FilterOption[] | FilterOption>,
    ) => void; // Callback when internal filter state changes
    onPaginationChange?: (pagination: PaginationState) => void;
    onResetFilters?: () => void;
    onRowClick?: (item: T) => void;
    onSearchChange?: (searchTerm: string) => void;
    onSelectedRowsChange?: (selectedRows: T[]) => void;
    onSortingChange?: (sorting: SortingState) => void;
    focusedCellId?: string | null;
  } & Pick<
    TableOptions<T>,
    'enableSortingRemoval' | 'enableMultiSort' | 'getRowId'
  >
>;

// what is available through the hook
export type DataTableContextValues<T> = Pick<
  DataTableContextProps<T>,
  | 'title'
  | 'loading'
  | 'filters'
  | 'onResetFilters'
  | 'defaultFilterValues'
  | 'controlledFilterState'
  | 'onFilterChange'
  | 'onFilterStateChange'
  | 'showHeader'
  | 'pageSizeOptions'
  | 'onRowClick'
  | 'ErrorPlaceholder'
  | 'NoDataPlaceholder'
  | 'EmptySearchResultsPlaceholder'
  | 'showError'
  | 'searchDebounceMs'
  | 'onSearchChange'
  | 'renderHeaderActions'
  | 'isEditing'
  | 'focusedCellId'
  | 'searchPlaceholder'
  | 'searchValue'
> & {
  getCommonPinningStyles: (
    column: Column<T>,
    isHeader?: boolean,
  ) => CSSProperties;
  filtersEnabled: boolean;
  paginationEnabled: boolean;
  searchEnabled: boolean;
  sortingEnabled: boolean;
  table: Table<T>;
  tableRef: RefObject<HTMLTableElement>;
  title?: string;
};

// Create the DataTable context
export const DataTableContext =
  createContext<DataTableContextValues<any> | null>(null);

// Create a custom hook to access the DataTable context
export const useDataTableContext = () => {
  const context = useContext(DataTableContext);

  if (!context) {
    throw new Error(
      'useDataTableContext must be used within a DataTableProvider',
    );
  }

  return context;
};

// Create a DataTableProvider component to wrap your DataTable component
// This provider acts as a 'settings' for all aspects of the table
// this allows us to make updates to different parts of the table without
// having to pass down props to each component
export const DataTableProvider = <T,>({
  children,
  columns,
  data,
  defaultFilterValues,
  controlledFilterState,
  EmptySearchResultsPlaceholder,
  enableFilters,
  enableMultiSort,
  enablePagination,
  enableSearch,
  searchPlaceholder,
  searchValue: controlledSearchValue,
  enableSortingRemoval,
  ErrorPlaceholder,
  filters,
  initialPageIndex,
  initialPageSize,
  pageIndex: controlledPageIndex,
  pageSize: controlledPageSize,
  loading,
  NoDataPlaceholder,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  rowCount,
  searchDebounceMs,
  showError,
  enableSorting,
  initialSorting,
  autoResetPageIndex = false,
  showHeader,
  title,
  pinColumns,
  isEditing,
  onUpdateData,
  renderHeaderActions,
  onFilterChange,
  onFilterStateChange,
  onPaginationChange,
  onResetFilters,
  onRowClick,
  onSearchChange,
  focusedCellId,
  getRowId,
}: DataTableContextProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex ?? 0,
    pageSize: initialPageSize ?? (pageSizeOptions[0] as number),
  });

  // Use controlled pagination if provided, otherwise use internal state
  const currentPagination: PaginationState =
    controlledPageIndex !== undefined
      ? {
          pageIndex: controlledPageIndex,
          pageSize: controlledPageSize ?? pagination.pageSize,
        }
      : pagination;

  // Pass controlled search value through context
  const currentSearchValue = controlledSearchValue;

  // Memoize the callbacks to prevent unnecessary re-renders
  const handleFilterChange = useCallbackRef(onFilterChange);
  const handlePaginationChange = useCallbackRef(onPaginationChange);
  const handleResetFilters = useCallbackRef(onResetFilters);
  const handleRowClick = useCallbackRef(onRowClick);
  const handleSearchChange = useCallbackRef(onSearchChange);

  const getCommonPinningStyles = useCallbackRef(
    (column: Column<T>, isHeader?: boolean): CSSProperties => {
      const isPinned = column.getIsPinned();
      const isLastLeftPinnedColumn =
        isPinned === 'left' && column.getIsLastColumn('left');
      const isFirstRightPinnedColumn =
        isPinned === 'right' && column.getIsFirstColumn('right');

      const getZIndex = () => {
        if (isHeader && isPinned) return 3;
        if (isHeader) return 2;
        if (isPinned) return 1;
        return 0;
      };

      return {
        borderRight: isLastLeftPinnedColumn ? '1px solid #D9DBDE' : undefined,
        borderLeft: isFirstRightPinnedColumn ? '1px solid #D9DBDE' : undefined,
        left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
        right:
          isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
        position: isPinned || isHeader ? 'sticky' : 'relative',
        width: column.getSize(),
        zIndex: getZIndex(),
      };
    },
    [],
  );

  const allColumns = useMemo(() => {
    const result = [...columns];
    return result;
  }, [columns]);

  // Listen for pagination changes
  useEffect(() => {
    if (controlledPageIndex === undefined) {
      handlePaginationChange?.(pagination);
    }
  }, [handlePaginationChange, pagination, controlledPageIndex]);

  const table = useReactTable<T>({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
    }),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    enableSorting: !!enableSorting,
    defaultColumn: { enableSorting: false },
    enableSortingRemoval,
    enableMultiSort,
    onSortingChange: setSorting,
    autoResetPageIndex,
    // For controlled pagination, we handle changes differently
    ...(controlledPageIndex !== undefined
      ? {
          manualPagination: true, // Tell TanStack this is server-side pagination
          onPaginationChange: (updaterOrValue) => {
            if (typeof updaterOrValue === 'function') {
              const newPagination = updaterOrValue(currentPagination);
              onPaginationChange?.(newPagination);
            } else {
              onPaginationChange?.(updaterOrValue);
            }
          },
        }
      : {
          onPaginationChange: setPagination,
        }),
    rowCount,
    enableColumnPinning: true,
    state: {
      pagination: enablePagination ? currentPagination : undefined,
      ...(enableSorting && { sorting }),
      ...(pinColumns && { columnPinning: pinColumns }),
    },
    enableColumnResizing: false,
    getRowId,
    meta: {
      updateData: (rowIndex: number, columnId: string, value: unknown) => {
        if (isEditing) {
          return onUpdateData?.(rowIndex, columnId, value);
        }
      },
    },
  });

  const tableRef = useRef<HTMLTableElement>(null);

  return (
    <DataTableContext.Provider
      value={{
        defaultFilterValues,
        controlledFilterState,
        filters,
        filtersEnabled: !!enableFilters,
        loading,
        pageSizeOptions,
        paginationEnabled: !!enablePagination,
        searchDebounceMs,
        searchEnabled: !!enableSearch,
        showError,
        showHeader,
        sortingEnabled: !!enableSorting,
        table,
        tableRef,
        title,
        searchPlaceholder,
        searchValue: currentSearchValue,
        isEditing: isEditing ?? false,
        focusedCellId,
        getCommonPinningStyles,
        renderHeaderActions,
        EmptySearchResultsPlaceholder,
        ErrorPlaceholder,
        NoDataPlaceholder,
        onFilterChange: handleFilterChange,
        onFilterStateChange,
        onRowClick: handleRowClick,
        onResetFilters: handleResetFilters,
        onSearchChange: handleSearchChange,
      }}
    >
      {children}
    </DataTableContext.Provider>
  );
};

export { createColumnHelper };

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData: (
      rowIndex: number,
      columnId: string,
      value: unknown,
    ) => void | Error;
  }
}
